/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useState, useMemo, useEffect, ChangeEvent } from "react";
import { When, If, Then, Else } from "react-if";
import {
  Sidebar,
  Table,
  Button,
  Switch,
  Input,
  Text,
  FlexContainer,
} from "@nordcloud/gnui";
import {
  useUpdateCostMappingRuleAlgorithmMutation,
  EnvCostShare,
  useDefaultApplicationQuery,
} from "~/generated/graphql";
import { UniversalWrap } from "~/components";
import { ERROR_TEXT, SUCCESS_TEXT } from "~/constants";
import { showSuccess, showError } from "~/services/toast";
import { isEmpty, isNotEmpty, noop } from "~/tools";
import { ApplicationOption } from "~/views/estate/components/types";
import { EnvSelectorRow } from "./EnvSelectorRow";
import { Footer, InputWrap } from "./styles";
import { CostSplittingEnvSelectorProps, CostMappingAlgorithm } from "./types";

export function CostSplittingEnvSelector({
  isOpen,
  onClose,
  applications,
  algorithm,
  onChange = noop,
  value,
  ruleId,
  saveDataOnly = false,
  envCostShares,
  setEnvCostShares,
}: CostSplittingEnvSelectorProps) {
  const environments = useMemo(() => value?.environments ?? [], [value]);
  const [isSelectedAllEnvironments, setIsSelectedAllEnvironments] = useState(
    value?.allEnvironments ?? false
  );

  const [selectedEnvironments, setSelectedEnvironments] = useState<
    EnvCostShare[]
  >(envCostShares ?? environments);

  const [filter, setFilter] = useState("");

  useEffect(() => {
    setSelectedEnvironments(changeDecimal(envCostShares ?? environments));
  }, [isOpen, environments]);

  useEffect(() => {
    setIsSelectedAllEnvironments(value?.allEnvironments ?? false);
  }, [value?.allEnvironments]);

  useEffect(() => {
    !isOpen && setFilter("");
  }, [setFilter, isOpen]);

  const { updateCostMappingRuleAlgorithm, loading: loadingUpdateCostMapping } =
    useUpdateCostMappingRuleAlgorithm();

  const onSave = () => {
    if (isNotEmpty(selectedEnvironments) || isSelectedAllEnvironments) {
      const costShares = changeDecimal(selectedEnvironments, false);

      if (envCostShares && setEnvCostShares) {
        setEnvCostShares(costShares);
      }

      closeSidebar();
    } else {
      showError(ERROR_TEXT.failedSettingCostSplittingEnv, { autoClose: false });
    }
  };

  const closeSidebar = () => {
    onClose();
    if (!envCostShares) {
      setSelectedEnvironments([]);
    }
  };

  const reset = () => {
    if (algorithm !== CostMappingAlgorithm.Manual) {
      setIsSelectedAllEnvironments(true);
    }

    setSelectedEnvironments([]);
  };

  const onSaveClick = async () => {
    if (saveDataOnly) {
      onSave();
    } else {
      await updateCostMappingRuleAlgorithm({
        isSelectedAllEnvironments,
        selectedEnvironments,
        ruleId,
        algorithm,
        onSuccess: onChange,
      });
    }
  };

  const {
    data: { defaultEnv, filteredApplications, mappedApplications },
    error,
    loading: loadingApplications,
  } = useData({ filter, applications });

  const { isUnderAllocated, totalUnallocatedCost, hasUnallocatedCost } =
    useUnallocatedCost({
      selectedEnvironments,
      defaultEnv,
    });

  const isSaveDisabled =
    algorithm === CostMappingAlgorithm.Manual && hasUnallocatedCost;

  return (
    <Sidebar
      isOpen={isOpen}
      title="Cost splitting target envs"
      footer={
        <Footer>
          <Button type="button" disabled={isSaveDisabled} onClick={onSaveClick}>
            Save
          </Button>
          <Button type="button" severity="low" onClick={reset}>
            Reset
          </Button>
        </Footer>
      }
      onClick={closeSidebar}
    >
      <UniversalWrap
        loaderProps={{
          loading: loadingApplications || loadingUpdateCostMapping,
          inContent: true,
        }}
        errorProps={{
          error,
        }}
      >
        <Input
          small
          type="filter"
          value={filter}
          name="filter"
          placeholder="Filter"
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setFilter(e.target.value)
          }
        />
        <When condition={!loadingApplications}>
          <If condition={algorithm !== CostMappingAlgorithm.Manual}>
            <Then>
              <FlexContainer justifyContent="flex-end" margin="1rem">
                <Switch
                  id="all-envs"
                  position="left"
                  labelText="All environments"
                  checked={isSelectedAllEnvironments}
                  onChange={() =>
                    setIsSelectedAllEnvironments((prevState) => !prevState)
                  }
                />
              </FlexContainer>
            </Then>
            <Else>
              <FlexContainer justifyContent="flex-end" margin="0.5rem">
                <When condition={isUnderAllocated !== 0}>
                  <Text tag="span" size="xs" color="red">
                    100% of your cost should be allocated to environments.
                  </Text>
                </When>
              </FlexContainer>
              <FlexContainer justifyContent="flex-end" margin="0.5rem">
                <Text size="md" tag="span">
                  Total unallocated costs
                </Text>
                <InputWrap>
                  <Input disabled value={totalUnallocatedCost || ""} />
                </InputWrap>
                %
              </FlexContainer>
            </Else>
          </If>
          <Table small>
            <When condition={!isSelectedAllEnvironments}>
              {filteredApplications.map((application) => (
                <EnvSelectorRow
                  key={application?.nid}
                  nid={application?.nid}
                  envs={selectedEnvironments}
                  name={application.name}
                  environments={application?.environments}
                  algorithm={algorithm ?? undefined}
                  applications={mappedApplications}
                  setEnvs={setSelectedEnvironments}
                />
              ))}
            </When>
          </Table>
        </When>
      </UniversalWrap>
    </Sidebar>
  );
}

function changeDecimal(env: EnvCostShare[], isPercent = true) {
  return env.map((e) => {
    const splitV = Number(e.splitValue) || 0;

    return splitV === 0
      ? e
      : {
          ...e,
          splitValue: isPercent ? String(splitV * 100) : String(splitV / 100),
        };
  });
}

function useData({
  filter,
  applications,
}: {
  filter: string;
  applications: ApplicationOption[] | undefined;
}) {
  const { data, loading, error } = useDefaultApplicationQuery({
    skip: applications != null,
  });

  const [mappedApplications, defaultEnv] = useMemo(() => {
    const mappedApps =
      applications ??
      (data?.applications ?? []).map((app) => {
        if (app?.name === "default") {
          const envs = app?.environments?.filter(
            (env) => env?.name !== "default"
          );

          return {
            id: app.id,
            nid: app.nid,
            name: app.name,
            environments: envs?.map((env) => ({
              id: env?.id ?? "",
              name: env?.name ?? "",
              nid: env?.nid ?? "",
            })),
          };
        }

        return app;
      });

    const defaultEnvironment =
      mappedApps.find((app) => app?.name === "default")?.environments?.[0]
        ?.id ?? "";

    // We use type assertion here to avoid filtering 1000s of items by `isNotNil`
    return [mappedApps as ApplicationOption[], defaultEnvironment];
  }, [data, applications]);

  const filteredApplications = useMemo(
    () =>
      mappedApplications.filter((app) =>
        filter.length > 2
          ? app.name.toLocaleLowerCase().includes(filter.toLocaleLowerCase())
          : true
      ),
    [filter, mappedApplications]
  );

  return {
    data: {
      mappedApplications,
      filteredApplications,
      defaultEnv,
    },
    loading,
    error,
  };
}

function useUnallocatedCost({
  selectedEnvironments,
  defaultEnv,
}: {
  selectedEnvironments: EnvCostShare[];
  defaultEnv: string;
}) {
  const totalUnallocatedCost =
    selectedEnvironments &&
    100 -
      selectedEnvironments
        .filter((e: EnvCostShare) => e?.id !== defaultEnv)
        .reduce(
          (total: number, env: EnvCostShare) =>
            total + Number(env.splitValue || "0"),
          0
        );

  const isUnderAllocated = totalUnallocatedCost && totalUnallocatedCost < 100;
  const hasUnallocatedCost = totalUnallocatedCost !== 0;

  return { totalUnallocatedCost, isUnderAllocated, hasUnallocatedCost };
}

function useUpdateCostMappingRuleAlgorithm() {
  const [runMutation, { loading }] =
    useUpdateCostMappingRuleAlgorithmMutation();

  const updateCostMappingRuleAlgorithm = async ({
    isSelectedAllEnvironments,
    selectedEnvironments,
    ruleId,
    onSuccess,
    algorithm,
  }: {
    isSelectedAllEnvironments: boolean;
    selectedEnvironments: EnvCostShare[];
    ruleId: string | undefined;
    algorithm: CostMappingAlgorithm | undefined;
    onSuccess: () => void;
  }) => {
    if (
      (isEmpty(selectedEnvironments) && !isSelectedAllEnvironments) ||
      ruleId === undefined
    ) {
      showError(ERROR_TEXT.failedSettingCostSplittingEnv, { autoClose: false });
      return;
    }

    const selectedEnvs = changeDecimal(selectedEnvironments || [], false);

    try {
      if (isSelectedAllEnvironments) {
        await runMutation({
          variables: {
            id: ruleId,
            algorithm,
            settings: {
              allEnvironments: isSelectedAllEnvironments,
            },
          },
        });
      } else {
        await runMutation({
          variables: {
            id: ruleId,
            algorithm,
            settings: {
              allEnvironments: isSelectedAllEnvironments,
              environments: selectedEnvs.map((env) => {
                return {
                  id: env.id,
                  splitValue: env.splitValue,
                };
              }),
            },
          },
        });
      }
      onSuccess();
      showSuccess(SUCCESS_TEXT.costSplittingChanged);
    } catch {
      showError(ERROR_TEXT.default);
    }
  };

  return { updateCostMappingRuleAlgorithm, loading };
}
